import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/rolePermissions";

/**
 * RoleBasedRoute - Protects routes based on roles and/or permissions
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Route content
 * @param {string[]} props.allowedRoles - Array of allowed roles (optional)
 * @param {string|string[]} props.requiredPermissions - Required permission(s) (optional)
 * @param {boolean} props.requireAllPermissions - If true, requires ALL permissions
 *
 * @example
 * // Role-based access
 * <RoleBasedRoute allowedRoles={['owner', 'admin']}>
 *   <SettingsPage />
 * </RoleBasedRoute>
 *
 * @example
 * // Permission-based access
 * <RoleBasedRoute requiredPermissions={PERMISSIONS.VIEW_ANALYTICS}>
 *   <AnalyticsPage />
 * </RoleBasedRoute>
 *
 * @example
 * // Both roles and permissions
 * <RoleBasedRoute
 *   allowedRoles={['owner', 'admin']}
 *   requiredPermissions={PERMISSIONS.MANAGE_SETTINGS}
 * >
 *   <SettingsPage />
 * </RoleBasedRoute>
 */
const RoleBasedRoute = ({
  children,
  allowedRoles,
  requiredPermissions,
  requireAllPermissions = false,
}) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admin has access to everything EXCEPT routes that explicitly exclude them
  const isSuperAdmin = user.role === 'super_admin';
  
  // Check role-based access if allowedRoles is provided
  // Super admin can access any role-restricted route
  const hasRoleAccess = !allowedRoles || allowedRoles.includes(user.role) || isSuperAdmin;

  // Check permission-based access if requiredPermissions is provided
  // hasPermission already returns true for super_admin
  const hasPermissionAccess =
    !requiredPermissions ||
    hasPermission(user.role, requiredPermissions, requireAllPermissions);

  // User must pass BOTH role and permission checks (if both are specified)
  if (!hasRoleAccess || !hasPermissionAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-red-500">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default RoleBasedRoute;
